/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { NvHigListInsertData } from '../../models/hig-list-item.types';
import { HyperLinkUserActivationGuardDirective } from '../../directives/hyperlink-user-activation-guard.directive';

@Component({
    selector: 'nv-hig-list-insert',
    templateUrl: './nv-hig-list-insert.component.html',
    styleUrls: ['./nv-hig-list-insert.component.scss'],
    imports: [
        NgClass,
        A11yModule,
        ExtendedModule,
        MatIconModule,
        MatButtonModule,
        TranslateModule,
        HyperLinkUserActivationGuardDirective
    ]
})
export class NvHigListInsertComponent implements OnChanges {
    @Input() data: NvHigListInsertData = {};
    @Input() textColor: string = 'font-color-primary';
    // --- Event Outputs from Child Components ---
    @Output() primaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClicked = new EventEmitter<MouseEvent>();
    @Output() iconButtonClicked = new EventEmitter<MouseEvent>();

    // Cached computed aria-label - updated only when data changes
    computedAriaLabel: string | undefined;

    constructor(private translateService: TranslateService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            const change = changes['data'];
            // Only regenerate if it's the first change or if relevant properties changed
            if (change.firstChange || this.hasAriaLabelRelatedChanges(change.previousValue, change.currentValue)) {
                this.computedAriaLabel = this.generateAriaLabel();
            }
        }
    }

    /**
     * Checks if properties relevant to aria-label generation have changed.
     * Only checks properties used in aria-label generation to avoid unnecessary recomputation.
     */
    private hasAriaLabelRelatedChanges(
        prev: NvHigListInsertData | undefined,
        curr: NvHigListInsertData | undefined
    ): boolean {
        if (!prev || !curr) {
            return true;
        }

        // Check if aria-label related properties changed
        return (
            prev.ariaLabel !== curr.ariaLabel ||
            prev.ariaRole !== curr.ariaRole ||
            prev.headerText?.text !== curr.headerText?.text ||
            prev.headerText?.translated !== curr.headerText?.translated ||
            prev.supportingText?.text !== curr.supportingText?.text ||
            prev.supportingText?.translated !== curr.supportingText?.translated
        );
    }

    /**
     * Generates aria-label from headerText and supportingText if not explicitly provided.
     * Combines both texts if present, or returns whichever is available.
     * Handles translation keys and already-translated text.
     */
    private generateAriaLabel(): string | undefined {
        // Use explicitly provided aria-label if available
        if (this.data?.ariaLabel) {
            return this.data.ariaLabel;
        }

        // Only auto-generate aria-label if ariaRole is set (i.e., it's an alert/status)
        if (!this.data?.ariaRole) {
            return undefined;
        }

        if (!this.data.headerText && !this.data.supportingText) {
            return undefined;
        }

        const headerLabel = this.data.headerText
            ? this.data.headerText.translated
                ? this.data.headerText.text
                : this.translateService.instant(this.data.headerText.text, this.data.headerText.placeholders)
            : '';

        const supportingLabel = this.data.supportingText
            ? this.data.supportingText.translated
                ? this.data.supportingText.text
                : this.translateService.instant(this.data.supportingText.text, this.data.supportingText.placeholders)
            : '';

        // Combine both if present, with a space separator
        if (headerLabel && supportingLabel) {
            return `${headerLabel} ${supportingLabel}`;
        }

        // Return whichever is available
        return headerLabel || supportingLabel;
    }

    // Passthrough Methods for Child Events
    // These methods simply re-emit the events from child components
    onPrimaryButtonClick(event: MouseEvent): void {
        this.primaryButtonClicked.emit(event);
    }
    onSecondaryButtonClick(event: MouseEvent): void {
        this.secondaryButtonClicked.emit(event);
    }
    onIconButtonClick(event: MouseEvent): void {
        this.iconButtonClicked.emit(event);
    }
}
